import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom, of, map } from 'rxjs';

import { OFFER_TYPE } from '../models/offer-utils';
import { OFFER_SERVER_URL } from '../../config';
import { UserService } from '../../shared/services/user.service';
import { DataWithTranslation, TranslationType } from '../../shared/models/data-with-translations';
import {
	Offer,
	OfferProduct,
	OfferLine,
	OfferStatus,
	OfferLineAggregation,
	BocOpt,
	OfferHeaderId,
	OfferItem,
	OfferDraft,
	FieldOfOfferItem,
	FieldOfOfferDraft
} from '../models/offer';
import { OfferDashboardResponse } from '../models/offer-dashboard';
import { safe } from '../../shared/utils/safe';
import { OrderBy } from '../../shared/utils/constant';
import { PaginatedResult } from '../../shared/models/paginated-result';
import {
	ProductCharacteristicsTranslationService
} from '../../shared/services/product-characteristics-translation.services';

const OFFER_LINES_URL = `${ OFFER_SERVER_URL }/offerLines`;
const OFFER_LINES_AGGREGATION_URL = `${ OFFER_SERVER_URL }/offerLines:aggregateByStatus`;
const OFFERS_URL = `${ OFFER_SERVER_URL }/offers`;
const OFFERS_DRAFT_URL = `${ OFFER_SERVER_URL }/offers:draft`;
const OFFERS_SUBMIT_URL = `${ OFFER_SERVER_URL }/v1/offers:submit`;
const OFFER_DASHBOARD_URL = `${ OFFER_SERVER_URL }/v1/offers:aggregate`;
const GET_OFFER_LINES_URL = `${ OFFER_SERVER_URL }/v1/offerLines`;
const DRAFT_OFFER_LINES_URL = `${ OFFER_SERVER_URL }/v1/draftoffers`;

export const OFFER_DRAFT_REDIRECT_URL = '/offer/draft';

@Injectable({ providedIn: 'root' })
export class OfferService {
	constructor(
		private readonly http: HttpClient,
		private readonly userService: UserService,
		private readonly productCharacteristicsTranslationService: ProductCharacteristicsTranslationService
	) {}

	getOfferLinePage(
		filter?: string,
		statuses?: OfferStatus[],
		activeOnly?: boolean,
		limit?: number,
		translationType?: TranslationType
	) {
		const url = new URL(OFFER_LINES_URL);
		const params = url.searchParams;
		if (filter) {
			params.append('filter', filter);
		}
		if (statuses) {
			params.append('status', statuses.join(','));
		}
		if (activeOnly) {
			params.append('active', String(activeOnly));
		}
		if (limit) {
			params.append('limit', String(limit));
		}
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		return this.http.get<PaginatedResult<OfferLine>>(url.toString());
	}

	getOffers(
		fields: readonly FieldOfOfferItem[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof OfferItem>[] | OrderBy<keyof OfferItem>,
		onlyDistinct: boolean
	) {
		const url = new URL(GET_OFFER_LINES_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		params.append('distinct', String(onlyDistinct));

		return this.http.get<PaginatedResult<OfferItem>>(url.toString());
	}

	getDraftOffers(
		fields: readonly FieldOfOfferDraft[],
		filter: string | undefined,
		index: number,
		limit: number,
		orderBy: OrderBy<keyof OfferDraft>[] | OrderBy<keyof OfferDraft>,
		onlyDistinct: boolean
	) {
		const url = new URL(DRAFT_OFFER_LINES_URL);
		const params = url.searchParams;
		params.append('fields', fields.join(','));
		if (filter) {
			params.append('filter', filter);
		}
		params.append('page', String(Math.floor(index / limit) + 1));
		params.append('limit', String(limit));

		params.append('orderBy', Array.isArray(orderBy) ? orderBy.join(',') : String(orderBy));

		params.append('distinct', String(onlyDistinct));

		return this.http.get<PaginatedResult<OfferDraft>>(url.toString());
	}

	async deleteDraftOffers(id: number) {
		const url = new URL(`${ DRAFT_OFFER_LINES_URL }/${ id }`);
		await firstValueFrom(this.http.delete(url.toString()));
	}

	getOfferLineAggregation(
		activeOnly?: boolean
	) {
		const aggregateUrl = new URL(OFFER_LINES_AGGREGATION_URL);
		const params = aggregateUrl.searchParams;
		if (activeOnly) {
			params.append('active', String(activeOnly));
		}
		return this.http.get<OfferLineAggregation[]>(aggregateUrl.toString());
	}

	getOfferDashboardResponse() {
		const url = new URL(OFFER_DASHBOARD_URL);
		return this.http.get<OfferDashboardResponse>(url.toString());
	}

	createOffer(
		offer: Offer
	) {
		offer.header.offerType = sessionStorage.getItem(OFFER_TYPE);
		const url = new URL(OFFERS_DRAFT_URL);
		return this.http.post<Offer>(
			url.toString(),
			this.createOfferWithoutCharacteristics(offer)
		);
	}

	updateOffer(
		offer: Offer
	) {
		if (!offer.header.offerType) {
			offer.header.offerType = sessionStorage.getItem(OFFER_TYPE);
		}
		const url = new URL(`${ OFFERS_URL }/${ safe(offer.header.id) }`);
		return this.http.put<Offer>(
			url.toString(),
			this.createOfferWithoutCharacteristics(offer)
		);
	}

	getOffer(
		offerId: string,
		withProducts = false,
		translationType?: TranslationType
	) {
		const url = new URL(`${ OFFERS_URL }/${ offerId }`);
		const params = url.searchParams;
		if (withProducts) {
			params.append('characteristics', String(withProducts));
		}
		if (translationType !== undefined) {
			params.append('translation', String(translationType));
		}
		return this.http.get<DataWithTranslation<Offer>>(url.toString()).pipe(map(offer => {
			this.productCharacteristicsTranslationService.add(offer.translations);
			return offer.data;
		}));
	}

	submitOffer(
		offer: Offer
	) {
		if (!offer.header.offerType) {
			offer.header.offerType = sessionStorage.getItem(OFFER_TYPE);
		}
		const url = new URL(OFFERS_SUBMIT_URL);
		return this.http.post<OfferHeaderId>(
			url.toString(),
			this.createOfferWithoutCharacteristics(offer)
		);
	}

	getOfferBocs(
		boc: BocOpt
	) {
		const bocIds = [];
		if (boc.soldTo) {
			bocIds.push(boc.soldTo);
		}
		if (boc.shipTo && boc.shipTo !== boc.soldTo) {
			bocIds.push(boc.shipTo);
		}
		if (boc.endUser
			&& boc.endUser !== boc.soldTo
			&& boc.endUser !== boc.shipTo
		) {
			bocIds.push(boc.endUser);
		}
		return this.getBocs(bocIds);
	}

	getBocs(
		businessIds: string[]
	) {
		if (businessIds.length === 0) {
			return of({
				results: [],
				count: 0,
				page: 1,
				limit: 0
			});
		}
		return this.userService.getBocs(
			['businessId', 'name', 'city'],
			businessIds.map(x => `businessId:${ x }`).join(' OR ')
		);
	}

	private createOfferWithoutCharacteristics(
		offer: Offer
	) {
		return {
			...offer,
			products: offer.products ? offer.products.map(x => this.createProductWithoutCharacteristics(x)) : undefined
		};
	}

	private createProductWithoutCharacteristics(
		product: OfferProduct
	) {
		return {
			...product,
			characteristics: undefined
		};
	}
}
